import Axios from 'axios'
import URL from 'src/api/url'
import { renderCard, clampProgress, renderSkeletonCards } from '../shared/card'
import { openYourLearningPopup, renderAdminNote } from './popup'

export const DEFAULT_TITLE = 'Pick up where you left off'
export const DEFAULT_LINK_LABEL = 'Your learning'

const SAMPLE_PAGE_SIZE = 20
const SAMPLE_CACHE_MS = 60 * 1000

let sampleRequest = null
let sampleRequestedAt = 0

/**
 * Sample rows for the editor preview. A creator has no progress of their own, so the endpoint
 * hands back the shop's own published content with fixed sample progress on it - the block and
 * the popup are otherwise identical to what a learner sees.
 *
 * The block is rebuilt on every settings change - each keystroke in the title field - so the
 * request is shared for a minute instead of being repeated for each rebuild.
 */
const fetchSample = () => {
  if (sampleRequest && Date.now() - sampleRequestedAt < SAMPLE_CACHE_MS) {
    return sampleRequest
  }

  sampleRequestedAt = Date.now()
  sampleRequest = Axios.post(URL.CONTINUE_LEARNING_ADMIN, {
    pageSize: SAMPLE_PAGE_SIZE,
  })
    .then(response => response.data?.data || response.data || [])
    .catch(error => {
      // A failed request must not stick for the next minute.
      sampleRequest = null
      throw error
    })

  return sampleRequest
}

const escapeHtml = value =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const ARROW_ICON = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
  <path d="M5 12h13m0 0-5-5m5 5-5 5" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

// Only plain colour values (hex, rgb/hsl, names) make it into the style attribute.
export const safeColor = value =>
  /^[#a-zA-Z0-9(),.%\s-]+$/.test(value || '') ? value : ''

// data-cl-title marks the heading initContinueLearningEvents makes editable in place.
const renderHeader = (title, linkLabel, titleColor) => `
  <div class="continue-learning-header">
    <h2 data-cl-title${
      safeColor(titleColor) ? ` style="color:${safeColor(titleColor)}"` : ''
    }>${escapeHtml(title)}</h2>
    <button class="continue-learning-link" type="button">
      <p>${escapeHtml(linkLabel)}</p>${ARROW_ICON}
    </button>
  </div>
`

/**
 * Preview of the Continue learning block. On the learner side the block lists only what that
 * learner started and hides itself when there is nothing; in the admin it is always visible
 * with sample content, which is what the note above it explains.
 */
const CreateContinueLearning = async (
  grid = '3',
  priceTagActive = false,
  {
    title = DEFAULT_TITLE,
    linkLabel = DEFAULT_LINK_LABEL,
    titleColor = '',
    live = false,
  } = {},
) => {
  let items = []
  try {
    items = await fetchSample()
  } catch (error) {
    console.error(
      'Continue learning fetch error:',
      error.response?.data || error.message,
    )
  }

  // The block is a single row: it shows exactly as many cards as the column setting, and
  // everything else is reachable from the popup.
  const cardCount = Math.max(1, parseInt(grid, 10) || 3)
  const inProgress = items.filter(item => clampProgress(item.progress) < 100)
  const visible = inProgress.slice(0, cardCount)

  // The page preview follows the app, where a learner with nothing in progress gets no block.
  if (live && !visible.length) {
    return { html: '', items, hidden: true }
  }

  const cards = visible.length
    ? `<div class="course-list-wrapper">${visible
        .map(item => renderCard(item, priceTagActive))
        .join('')}</div>`
    : renderSkeletonCards(cardCount, { withProgress: true })

  const html = `
    <div class="continue-learning-wrapper" contenteditable="false">
      ${renderAdminNote(items.length > 0)}
      ${renderHeader(title, linkLabel, titleColor)}
      ${cards}
    </div>
  `

  return { html, items }
}

/**
 * Lets the heading be typed over in place, like the heading above a catalog or course list.
 * The widget is one object to CKEditor, which would otherwise swallow the keystrokes, so the
 * heading stops its own events. The new title is committed on Enter or when the heading loses
 * focus - committing on every keystroke would rebuild the block, and the caret with it.
 */
const initEditableTitle = (container, onTitleChange) => {
  const heading = container.querySelector('[data-cl-title]')
  if (!heading) return

  heading.setAttribute('contenteditable', 'true')
  heading.setAttribute('data-cke-ignore-events', 'true')
  heading.setAttribute('spellcheck', 'false')

  ;[
    'keydown',
    'keyup',
    'keypress',
    'input',
    'paste',
    'cut',
    'mousedown',
    'click',
    'focus',
    'beforeinput',
  ].forEach(type => {
    heading.addEventListener(type, event => event.stopPropagation())
  })

  const initial = heading.textContent

  heading.addEventListener('keydown', event => {
    if (event.key === 'Enter') {
      event.preventDefault()
      heading.blur()
    } else if (event.key === 'Escape') {
      heading.textContent = initial
      heading.blur()
    }
  })

  // Plain text only - pasted formatting would otherwise land in the heading.
  heading.addEventListener('paste', event => {
    event.preventDefault()
    const text = (event.clipboardData || window.clipboardData).getData('text')
    document.execCommand('insertText', false, text.replace(/\s+/g, ' '))
  })

  heading.addEventListener('blur', () => {
    const next = heading.textContent.replace(/\s+/g, ' ').trim()
    if (!next) {
      heading.textContent = initial
      return
    }
    if (next !== initial) onTitleChange(next)
  })
}

/**
 * Wires the block's plain-DOM parts once its HTML lands. The heading is only made editable where
 * an onTitleChange handler is given - the editor - and stays a plain heading in the page preview.
 */
export const initContinueLearningEvents = (
  container,
  { items = [], onTitleChange } = {},
) => {
  if (!container) return

  if (onTitleChange) initEditableTitle(container, onTitleChange)

  const link = container.querySelector('.continue-learning-link')
  if (!link) return

  link.style.cursor = 'pointer'
  ;['mousedown', 'keydown', 'keyup'].forEach(type => {
    link.addEventListener(type, event => event.stopPropagation())
  })

  link.addEventListener('click', event => {
    event.preventDefault()
    event.stopPropagation()
    // The popup sits on document.body, outside the block, so it takes the page's primary
    // colour along explicitly.
    const primaryColor = getComputedStyle(container)
      .getPropertyValue('--page-accent')
      .trim()
    openYourLearningPopup({ items, primaryColor })
  })
}

export default CreateContinueLearning
